import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { AnalyticsService, StatCard, getGeneralStatCards, isCompletionEnabled, isScoringEnabled, SessionData, CourseAnalyticsTab, chartColorPalette } from '@shared/services/analytics.service';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { Course } from '@shared/models/course.model';
import { Annotation } from '@shared/models/annotation.model';
import { Scene } from '@shared/models/scene.model';
import { GoogleChartInterface } from 'ng2-google-charts';
import { LoadState } from '@app/main/analytics/loader-directive/loader.module';
@Component({
  selector: 'course-stats',
  templateUrl: './course-stats.component.html',
  styleUrls: ['./course-stats.component.scss']
})

export class CourseStatsComponent implements OnInit, OnChanges {
  @Input() course: Course;
  @Input() quizzes: Annotation[];
  @Input() date_from: Date;
  @Input() date_to: Date;
  @Output() navigateTab = new EventEmitter<{ name: string, tab?: CourseAnalyticsTab }>();


  // TODO: Interface
  public statsCards: StatCard[];

  public trafficChart: GoogleChartInterface;
  public contentChart: GoogleChartInterface;
  public browserChart: GoogleChartInterface;
  public sourceChart: GoogleChartInterface;
  public osChart: GoogleChartInterface;
  public latestSessionsData: SessionData[];

  public courseDataStatus = LoadState.pending;
  public trafficDataStatus = LoadState.pending;
  public contentDataStatus = LoadState.pending;
  public browserDataStatus = LoadState.pending;
  public sourceDataStatus = LoadState.pending;
  public osDataStatus = LoadState.pending;
  public latestSessionDataStatus = LoadState.pending;

  public chartColor = '#247e8d';
  public mediaCount: number;
  constructor(
    private _analyticsService: AnalyticsService,
    private _courseAPIService: CoursesAPIService
  ) {

  }

  async ngOnInit(): Promise<void> {
    const _mediaCount = await this._courseAPIService.countCourseMedias(this.course.id).toPromise();
    this.mediaCount = _mediaCount.count || 0;
    this.initCharts();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.date_from && !changes.date_from.isFirstChange() && changes.date_from.currentValue !== changes.date_from.previousValue)
      || (changes.date_to && !changes.date_to.isFirstChange() && changes.date_to.currentValue !== changes.date_to.previousValue)) {
      this.initCharts();
    }
  }

  formatSceneViewsChartData(scenes: Scene[], stats: any[]): any[] {
    let colorPaletteIndex = 0;
    const data = [['', '', { role: 'annotation' }, { role: 'style' }]];
    for (let scene of scenes) {
      if (colorPaletteIndex === chartColorPalette.length) colorPaletteIndex = 0;
      const color = chartColorPalette[colorPaletteIndex++];
      const sceneViews = stats.find((s: any) => s.scene_id === scene.id)?.views || 0;
      const title = scene.title.length > 20 ? (scene.title as string).substring(0, 20) + "...": scene.title;
      data.push([scene.title, sceneViews, title, color])
    }
    return data;

  }
  formatBarChartData(label: string, stats: any[]): any[] {
    let colorPaletteIndex = 0;
    const data = [[label, '', { role: 'annotation' }, { role: 'style' }]];
    const total = stats.reduce((sum, stat) => sum + stat.count, 0);
    stats.map((stat) => {
      if (colorPaletteIndex === chartColorPalette.length) colorPaletteIndex = 0;
      const color = chartColorPalette[colorPaletteIndex++];
      data.push([stat._id, stat.count, Math.round(stat.count / total * 100) + '%', color]);
    });
    return data;
  }
  formatSourceBarChartData(label: string, stats: any[]): any[] {
    let colorPaletteIndex = 0;
    const data = [[label, '', { role: 'annotation' }, { role: 'tooltip' }, { role: 'style' }]];
    const total = stats.reduce((sum, stat) => sum + stat.count, 0);
    stats.map((stat) => {
      if (colorPaletteIndex === chartColorPalette.length) colorPaletteIndex = 0;
      const color = chartColorPalette[colorPaletteIndex++];
      let displayedLabel = (stat._id && stat._id !== '') ? stat._id.split('?')[0] : 'Direct Link';
      displayedLabel = (displayedLabel.length > 60) ? displayedLabel.substring(0, 60) + '...' : displayedLabel;
      data.push([displayedLabel, stat.count, Math.round(stat.count / total * 100) + '%', stat._id, color]);
    });
    return data;
  }

  async updateTrafficChart(): Promise<void> {

    this.trafficDataStatus = LoadState.loading;
    try {
      const stats = await this._analyticsService.getCourseTraffic(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();

      if (!stats || stats.length === 0) {
        this.trafficDataStatus = LoadState.empty;
        return;
      }
      this.trafficDataStatus = LoadState.completed;
      const data = [[{ type: 'date' }, '']];
      stats.map((stat: any) => {
        data.push([new Date(stat.model), stat.count]);
      });

      const dateFormat = stats.length > 31 ? 'MMM' : 'MMM dd';
      this.trafficChart = {
        chartType: 'AreaChart',
        dataTable: data,
        options: {
          legend: { position: 'none' },
          colors: [this.chartColor],
          chartArea: { width: '100%' },
          hAxis: {
            format: dateFormat
          }
        }
      };
    }
    catch (e) {
      this.trafficDataStatus = LoadState.error;
    }

  }

  async initCharts(): Promise<void> {
    await this.updateStatCards();

    // Init Traffic
    await this.updateTrafficChart();
    await this.updateLatestSessionsWidget();
    await this.updateContentChart();
    await this.updateBarCharts();


  }
  async updateContentChart(): Promise<void> {
    let stats;
    let scenes;
    this.contentDataStatus = LoadState.loading;
    try {
      scenes = await this._courseAPIService.getCourseScenes(this.course.id).toPromise();
      stats = await this._analyticsService.getSceneViewsStats(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!scenes || scenes.length === 0) {
        this.contentDataStatus = LoadState.empty;
        return;
      }
      const data = this.formatSceneViewsChartData(scenes, stats);

      this.contentChart = {
        chartType: 'ColumnChart',
        dataTable: data,
        options: {
          chartArea: { width: '100%', height: '100%' },
          legend: { position: 'none' },
          colors: chartColorPalette
        }
      };
      this.contentDataStatus = LoadState.completed;
    }
    catch (e) {
      this.contentDataStatus = LoadState.error;
    }
  }
  async updateBarCharts(): Promise<void> {
    await this.updateBrowserChart();
    await this.updateOSChart();
    await this.updateSourceChart();
  }
  async updateBrowserChart(): Promise<void> {
    let stats;
    // Init browser char
    this.browserDataStatus = LoadState.loading;
    try {
      stats = await this._analyticsService.getLaunchByBrowser(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!stats || stats.length === 0) {
        this.browserDataStatus = LoadState.empty;
        return;
      }
      this.browserChart = {
        chartType: 'BarChart',
        dataTable: this.formatBarChartData('Browser', stats),
        options: {
          chartArea: { width: '50%', height: '100%' },
          legend: { position: 'none' },
          colors: chartColorPalette
        }
      };
      this.browserDataStatus = LoadState.completed;
    }
    catch (e) {
      this.browserDataStatus = LoadState.error;
    }

  }
  async updateOSChart(): Promise<void> {
    let stats;
    // Init OS Chart
    this.osDataStatus = LoadState.loading;
    try {
      stats = await this._analyticsService.getLaunchByOS(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!stats || stats.length === 0) {
        this.osDataStatus = LoadState.empty;
        return;
      }

      this.osChart = {
        chartType: 'BarChart',
        dataTable: this.formatBarChartData('OS', stats),
        options: {
          chartArea: { width: '50%', height: '100%' },
          legend: { position: 'none' },
          colors: chartColorPalette
        }
      };
      this.osDataStatus = LoadState.completed;
    }
    catch (e) {
      this.osDataStatus = LoadState.error;
    }
  }
  async updateSourceChart(): Promise<void> {
    let stats;
    // // Init source Chart
    this.sourceDataStatus = LoadState.loading;
    try {
      stats = await this._analyticsService.getLaunchBySource(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      if (!stats || stats.length === 0) {
        this.sourceDataStatus = LoadState.empty;
        return;
      }

      this.sourceChart = {
        chartType: 'BarChart',
        dataTable: this.formatSourceBarChartData('Source', stats),
        options: {
          chartArea: { width: '30%', height: '100%' },
          legend: { position: 'none' },
          colors: [this.chartColor]
        }
      };
      this.sourceDataStatus = LoadState.completed;
    }
    catch (e) {
      this.sourceDataStatus = LoadState.error;
    }
  }
  async updateStatCards(): Promise<void> {
    let stats;
    // Init statcards
    this.courseDataStatus = LoadState.loading;
    try {
      stats = await this._analyticsService.getCourseStats(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to }).toPromise();
      this.courseDataStatus = LoadState.completed;
      this.statsCards = [
        {
          icon: 'fa-photo-film',
          label: `<span class="figure">${this.mediaCount}</span>&nbsp;<span class="label">medias</span>`,
          sublabel: `${this.quizzes.length} quizzes`
        },
        ...getGeneralStatCards(stats, isCompletionEnabled(this.course), isScoringEnabled(this.course))
      ];
    }
    catch (e) {
      this.courseDataStatus = LoadState.error;
    }
  }
  async updateLatestSessionsWidget(): Promise<void> {
    let stats;
    // Init statcards
    this.latestSessionDataStatus = LoadState.loading;
    try {
      stats = await this._analyticsService.getSessions(this.course.id, { dateFrom: this.date_from, dateTo: this.date_to, offset: 0, limit: 6 }).toPromise();
      this.latestSessionDataStatus = LoadState.completed;
      this.latestSessionsData = stats as SessionData[];
    }
    catch (e) {
      this.latestSessionDataStatus = LoadState.error;
    }
  }
  showUserSessions(event: { name: string, tab?: CourseAnalyticsTab }) {
    this.navigateTab.emit(event);
  }
  goToContentTab() {
    this.navigateTab.emit({ name: null, tab: CourseAnalyticsTab.Content });
  }
  goToSessionTab() {
    this.navigateTab.emit({ name: null, tab: CourseAnalyticsTab.Sessions });
  }

}
